import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Upload, Image as ImageIcon, Video } from "lucide-react";
// import adminApi from "../services/adminApi";   // Adjust path as needed
import api from "../../../../services/api";
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.35)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  zIndex: 999,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const modalStyle = {
  width: "100%",
  maxWidth: "700px",
  background: "#fff",
  borderRadius: "20px",
  padding: "36px 36px 28px",
  position: "relative",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  maxHeight: "90vh",
  overflowY: "auto",
};

export default function UploadMediaPopup({
  onClose,
  categories = [],
  editData = null,
  onUpdate = null
}) {
  const queryClient = useQueryClient();
  const isEditMode = !!editData;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      authorName: "",
      category_id: "",
    },
  });

  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  // Populate form in Edit Mode
  useEffect(() => {
    if (isEditMode && editData) {
      reset({
        title: editData.title || "",
        authorName: editData.author || "",
        category_id: editData.category_id || editData.category || "",
      });

      if (editData.thumbnail) setThumbnailPreview(editData.thumbnail);
      if (editData.media_url) setVideoPreview(editData.media_url);
    }
  }, [isEditMode, editData, reset]);

  // Create Mutation
  const createMutation = useMutation({
    mutationFn: (formData) => api.createMediaItem(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faith-media-items"] });
      onClose?.();
    },
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => api.updateMediaItem(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faith-media-items"] });
      onClose?.();
      onUpdate?.();
    },
  });

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("author", data.authorName);
    formData.append("category_id", data.category_id);

    if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
    if (videoFile) formData.append("media", videoFile);

    if (isEditMode && editData?.id) {
      // Update existing media
      updateMutation.mutate({
        id: editData.id,
        formData
      });
    } else {
      // Create new media
      createMutation.mutate(formData);
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
 <button
  type="button"
  onClick={onClose}
  style={{
    position: "absolute",
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#F3F4F6",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    outline: "none",
    transition: "all 0.2s ease",
    zIndex: 10,
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = "#E5E7EB";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = "#F3F4F6";
  }}
>
  <X
    size={18}
    color="#6B7280"
    strokeWidth={2.5}
  />
</button>

        <h2 style={{ fontSize: 24, fontWeight: 700, textAlign: "center", marginBottom: 28 }}>
          {isEditMode ? "Edit Faith Stream" : "New Faith Stream"}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              {...register("title", { required: "Title is required" })}
              placeholder="Enter video title"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#1F3A60]"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Author Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("authorName", { required: "Author name is required" })}
              placeholder="Enter author name"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#1F3A60]"
            />
            {errors.authorName && <p className="text-red-500 text-sm mt-1">{errors.authorName.message}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Select Category <span className="text-red-500">*</span>
            </label>
            <select
              {...register("category_id", { required: "Please select a category" })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#1F3A60] bg-white"
            >
              <option value="">Select a category</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {errors.category_id && <p className="text-red-500 text-sm mt-1">{errors.category_id.message}</p>}
          </div>

          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Thumbnail {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <div
              onClick={() => document.getElementById("thumbnail-input").click()}
              className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-[#1F3A60]"
            >
              {thumbnailPreview ? (
                <img
                  src={thumbnailPreview.startsWith("/uploads") ? `${API_ROOT}${thumbnailPreview}` : thumbnailPreview}
                  alt="preview" className="max-h-40 mx-auto rounded-xl" />
              ) : (
                <div>
                  <ImageIcon size={48} className="mx-auto text-gray-400" />
                  <p className="mt-3 text-sm text-gray-600">Drop or click to upload thumbnail</p>
                </div>
              )}
            </div>
            <input
              id="thumbnail-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleThumbnailChange}
            />
          </div>

          {/* Video */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Video {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <div
              onClick={() => document.getElementById("video-input").click()}
              className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-[#1F3A60]"
            >
              {videoPreview ? (
                <video
                  src={videoPreview.startsWith("/uploads") ? `${API_ROOT}${videoPreview}` : videoPreview}
                  controls className="max-h-48 mx-auto rounded-xl" />
              ) : (
                <div>
                  <Video size={48} className="mx-auto text-gray-400" />
                  <p className="mt-3 text-sm text-gray-600">Drop or click to upload video</p>
                </div>
              )}
            </div>
            <input
              id="video-input"
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleVideoChange}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
              className="px-8 py-3 bg-[#E8C547] hover:bg-[#d4b03d] font-semibold rounded-xl flex items-center gap-2 disabled:opacity-70"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              )}
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Upload Media"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}